import { useMemo } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'
import i18n from 'modules/core/lib/i18n'
import { tokens } from '@factorialco/deprecated-design-system'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { DEFAULT_CURRENCY } from 'modules/finance/components/FinancialWorkspace/components/Table/const'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'

interface UseDataCollectionColumnsOptions {
  headers: string[]
  groupBy: InsightTableGroupBy
  showComparison?: boolean
}

/**
 * Generates dynamic column definitions for OneDataCollection table
 * Handles both regular and comparison modes with appropriate column structures
 */
export const useDataCollectionColumns = ({
  headers,
  groupBy,
  showComparison = false
}: UseDataCollectionColumnsOptions) => {
  const { company } = useCurrentSession()
  const { supportedCurrencies } = useFinancialWorkspace()

  const currency = supportedCurrencies.includes(String(company.currency))
    ? company.currency
    : DEFAULT_CURRENCY

  const locale = company.locale ?? 'en'

  return useMemo(() => {
    const formatAmount = (amountInCents: string | undefined): string => {
      const amount = Number(amountInCents) || 0
      if (!isCurrency(String(currency))) return '-'
      return MoneyWithCurrency({
        amount,
        currency: String(currency),
        locale,
        optionalDecimals: false
      })
    }

    const labelColumn = {
      label: '',
      width: 300,
      render: (item: FinancialWorkspaceRecord) => {
        if (groupBy === InsightTableGroupBy.Employee && !item.isChild) {
          const nameParts = (item.label ?? '').split(' ')
          const firstName = nameParts[0] ?? ''
          const lastName = nameParts.slice(1).join(' ') ?? ''
          return {
            type: 'person' as const,
            value: {
              firstName,
              lastName,
              src: item.imageUrl
            }
          }
        }

        return {
          type: 'text' as const,
          value: item.label ?? '-'
        }
      }
    }

    if (headers.length === 0) {
      return [labelColumn]
    }

    // In comparison mode, useHeaders() provides 3 headers per period:
    // [comparison_date, current_date, "Growth", ...] so we create one column per header
    const periodColumns = headers.map((headerLabel, index) => {
      if (showComparison) {
        const positionInTriplet = index % 3

        if (positionInTriplet === 0) {
          // Comparison period column
          return {
            id: `comparison-${index}`,
            label: headerLabel,
            align: 'right' as const,
            width: 120,
            render: (item: FinancialWorkspaceRecord) => {
              const comparisonValue = item.comparisonValues?.find((cv) => cv.label === headerLabel)
              return {
                type: 'text' as const,
                value: formatAmount(comparisonValue?.amountInCents)
              }
            }
          }
        } else if (positionInTriplet === 1) {
          // Current period column
          return {
            id: `current-${index}`,
            label: headerLabel,
            align: 'right' as const,
            width: 120,
            render: (item: FinancialWorkspaceRecord) => {
              const currentValue = item.periodValues?.find((pv) => pv.label === headerLabel)
              return {
                type: 'text' as const,
                value: formatAmount(currentValue?.amountInCents)
              }
            }
          }
        } else {
          // Growth column: calculate percentage change
          const currentLabel = headers[index - 1]
          const comparisonLabel = headers[index - 2]

          return {
            id: `growth-${index}`,
            label: headerLabel,
            align: 'right' as const,
            width: 100,
            render: (item: FinancialWorkspaceRecord) => {
              const currentValue =
                Number(item.periodValues?.find((pv) => pv.label === currentLabel)?.amountInCents) ||
                0
              const comparisonValue =
                Number(
                  item.comparisonValues?.find((cv) => cv.label === comparisonLabel)?.amountInCents
                ) || 0

              if (!comparisonValue || comparisonValue === 0) {
                return {
                  type: 'text' as const,
                  value: '-'
                }
              }

              const percentage =
                ((currentValue - comparisonValue) / Math.abs(comparisonValue)) * 100
              const absPercentage = Math.abs(percentage).toFixed(2)

              if (absPercentage === '0.00') {
                return {
                  type: 'text' as const,
                  value: '-'
                }
              }

              const isPositive = percentage > 0
              const arrow = isPositive ? '↑' : '↓'
              const customColor = isPositive ? '#07A2AD' : '#FF355E'

              return {
                type: 'tag' as const,
                value: {
                  customColor,
                  label: `${arrow} ${absPercentage}%`,
                  icon: isPositive ? 'arrow-up' : 'arrow-down'
                }
              }
            }
          }
        }
      }

      // Non-comparison mode: single column per period
      return {
        id: `period-${index}`,
        label: headerLabel,
        align: 'right' as const,
        width: 120,
        render: (item: FinancialWorkspaceRecord) => {
          const value = item.periodValues?.find((pv) => pv.label === headerLabel)
          if (!value) {
            return {
              type: 'text' as const,
              value: '-'
            }
          }

          return {
            type: 'text' as const,
            value: formatAmount(value.amountInCents)
          }
        }
      }
    })

    return [labelColumn, ...periodColumns]
  }, [headers, groupBy, showComparison, currency, locale])
}
